import React from 'react';
import { Pressable, View, StyleSheet, Platform, PanResponder } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useTheme } from '@/context/ThemeContext';
import { useAudioPlayer, useAudioPlayerStatus, setAudioModeAsync } from 'expo-audio';
import { Svg, Rect, Line } from 'react-native-svg';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { seekRatioFromClientX } from '@/constants/utils/audio_seek';

const createStyles = (colors) => StyleSheet.create({
  root: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  inner: {
    width: '100%',
    paddingHorizontal: 8,
    gap: 6,
  },
  waveArea: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlsRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 3,
  },
  playButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 3,
  },
  volumeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
  },
  speedButton: {
    minWidth: 42,
    height: 32,
    paddingHorizontal: 7,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
  },
  trackWrapper: {
    flex: 1,
    height: 12,
    justifyContent: 'center',
  },
  trackBg: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.solid_color_secondary,
    // overflow: 'hidden',
    position: 'relative',
  },
  trackFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.text_color_primary,
  },
  trackKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.text_color_primary,
    ...Platform.select({
      web: { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.25)' },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.25,
        shadowRadius: 2,
        elevation: 2,
      },
    }),
  },
  trackGhostKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: colors.text_color_primary,
    opacity: 0.6,
    ...Platform.select({
      web: { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)' },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
});

const AudioWaveformPlayer = React.forwardRef(function AudioWaveformPlayer({
  sourceUrl,
  size,
  file,
  isLarge = false,
  autoPlay = false,
  initialPositionSeconds = 0,
  playbackRate: controlledPlaybackRate,
  onPlaybackRateChange,
  onPlaybackStart,
  onPlaybackEnd,
}, ref) {
  const { colors } = useTheme();
  const styles = React.useMemo(() => createStyles(colors), [colors]);
  const isFocused = useIsFocused();
  
  // expo-audio hooks - useAudioPlayer creates and manages the player
  const player = useAudioPlayer(sourceUrl || undefined);
  const status = useAudioPlayerStatus(player);
  
  // Convert seconds to milliseconds for backward compatibility with UI
  const isPlaying = status?.playing || false;
  const positionMillis = (status?.currentTime || 0) * 1000;
  const durationMillis = (status?.duration || 0) * 1000;
  
  const [volume, setVolume] = React.useState(1);
  const [internalPlaybackRate, setInternalPlaybackRate] = React.useState(1);
  const [trackWidth, setTrackWidth] = React.useState(0);
  const [waveWidth, setWaveWidth] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragRatio, setDragRatio] = React.useState(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const [hoverRatio, setHoverRatio] = React.useState(null);
  const trackBgRef = React.useRef(null);
  const dragRatioRef = React.useRef(null);
  const didNotifyEndRef = React.useRef(false);
  const didNotifyStartRef = React.useRef(false);
  const playbackStartedRef = React.useRef(false);
  const initialSeekSourceRef = React.useRef(null);
  const playbackRate = controlledPlaybackRate ?? internalPlaybackRate;

  const startPlayback = React.useCallback(() => {
    if (!player) return;
    didNotifyEndRef.current = false;
    didNotifyStartRef.current = false;
    playbackStartedRef.current = false;
    if (player.duration > 0 && player.currentTime >= player.duration) player.seekTo(0);
    player.play();
  }, [player]);

  // Ensure audio plays even when iPhone silent switch is on
  React.useEffect(() => {
    setAudioModeAsync({ playsInSilentMode: true }).catch(() => {});
  }, []);

  // Set initial volume when player is ready
  React.useEffect(() => {
    if (player) {
      player.volume = volume;
    }
  }, [player, volume]);

  React.useEffect(() => {
    if (!player) return;
    try {
      player.setPlaybackRate(playbackRate, 'high');
    } catch (_) {}
  }, [playbackRate, player]);

  React.useEffect(() => {
    if (
      !player ||
      !sourceUrl ||
      !status?.isLoaded ||
      initialPositionSeconds <= 0 ||
      initialSeekSourceRef.current === sourceUrl
    ) return;
    try {
      player.seekTo(initialPositionSeconds);
      initialSeekSourceRef.current = sourceUrl;
    } catch (_) {}
  }, [initialPositionSeconds, player, sourceUrl, status?.isLoaded]);

  React.useEffect(() => {
    didNotifyEndRef.current = false;
    didNotifyStartRef.current = false;
    playbackStartedRef.current = false;
    if (
      !autoPlay ||
      !player ||
      !sourceUrl ||
      (initialPositionSeconds > 0 && !status?.isLoaded)
    ) return undefined;
    const timer = setTimeout(() => {
      try { startPlayback(); } catch (_) {}
    }, 50);
    return () => clearTimeout(timer);
  }, [autoPlay, initialPositionSeconds, player, sourceUrl, startPlayback, status?.isLoaded]);

  React.useEffect(() => {
    if (status?.playing || status?.currentTime > 0) {
      playbackStartedRef.current = true;
    }
    if (status?.playing && !didNotifyStartRef.current) {
      didNotifyStartRef.current = true;
      onPlaybackStart?.();
    }
    if (!status?.didJustFinish || !playbackStartedRef.current || didNotifyEndRef.current) return;
    didNotifyEndRef.current = true;
    try {
      player?.pause();
      player?.seekTo(0);
    } catch (_) {}
    onPlaybackEnd?.();
  }, [onPlaybackEnd, onPlaybackStart, player, status?.currentTime, status?.didJustFinish, status?.playing]);

  // Cleanup: pause audio when component unmounts (e.g., navigating away)
  React.useEffect(() => {
    return () => {
      if (player) {
        try {
          player.pause();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [player]);

  // Pause playback when the screen loses focus (navigate away)
  React.useEffect(() => {
    if (!isFocused && player) {
      try {
        player.pause();
      } catch (e) {
        // Ignore focus pause errors
      }
    }
  }, [isFocused, player]);

  const togglePlayPause = React.useCallback(async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!sourceUrl || !player) {
      console.log('togglePlayPause no sourceUrl or player');
      return;
    }
    try {
      if (isPlaying) {
        player.pause();
      } else {
        startPlayback();
      }
    } catch (err) {
      // console.log('togglePlayPause error', err);
    }
  }, [sourceUrl, player, isPlaying, startPlayback]);

  const pauseAudio = React.useCallback(() => {
    if (!player) return;
    try {
      player.pause();
    } catch (e) {}
  }, [player]);

  const stopAudio = React.useCallback(() => {
    if (!player) return;
    try {
      player.pause();
      player.seekTo(0);
    } catch (e) {}
  }, [player]);

  const playAudio = React.useCallback(() => {
    if (!player || !sourceUrl) return;
    try {
      startPlayback();
    } catch (e) {}
  }, [player, sourceUrl, startPlayback]);

  React.useImperativeHandle(ref, () => ({
    play: playAudio,
    pause: pauseAudio,
    stop: stopAudio,
    seekTo: (seconds) => player?.seekTo?.(seconds),
    getPosition: () => status?.currentTime || 0,
    getDuration: () => status?.duration || 0,
    isPlaying: () => isPlaying,
  }), [playAudio, pauseAudio, stopAudio, player, status?.currentTime, status?.duration, isPlaying]);

  const formatTime = React.useCallback((ms) => {
    if (!ms || ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s < 10 ? '0' + s : s}`;
  }, []);

  const baseBars = size >= 300 ? 64 : 40;
  const numBars = isLarge ? baseBars * 2 : baseBars;
  const progressRatio = durationMillis > 0 ? Math.max(0, Math.min(1, positionMillis / durationMillis)) : 0;
  const displayRatio = isDragging && typeof dragRatio === 'number' ? dragRatio : progressRatio;

  const barData = React.useMemo(() => {
    // Deterministic pseudo-waveform from file identity
    const key = `${file?.name || ''}|${file?.size || file?.sizeInBytes || 0}|${file?.cdnPath || file?.url || ''}`;
    let seed = 0;
    for (let i = 0; i < key.length; i++) {
      seed = (seed * 31 + key.charCodeAt(i)) >>> 0;
    }
    const rand = () => {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) % 1000) / 1000;
    };
    const out = [];
    for (let i = 0; i < numBars; i++) {
      const base = rand() * 0.8 + 0.2; // 0.2 - 1.0
      const ease = Math.sin((i / numBars) * Math.PI);
      out.push(Math.min(1, base * (0.5 + 0.5 * ease)));
    }
    return out;
  }, [file?.name, file?.size, file?.sizeInBytes, file?.cdnPath, file?.url, numBars]);

  const waveHeight = Math.max(
    20,
    Math.min(isLarge ? 200 : 40, Math.round(size * (isLarge ? 0.18 : 0.12)))
  );
  const volumeIcon = volume === 0 ? 'volume-xmark' : (volume < 0.75 ? 'volume-low' : 'volume-high');

  const cycleVolume = React.useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!player) return;
    const next = volume === 1 ? 0.5 : (volume === 0.5 ? 0 : 1);
    setVolume(next);
    try {
      player.volume = next;
    } catch (e) {
      // console.log('cycleVolume error', e);
    }
  }, [player, volume]);

  const cyclePlaybackRate = React.useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const rates = [0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const next = rates[(currentIndex + 1) % rates.length];
    if (controlledPlaybackRate == null) setInternalPlaybackRate(next);
    onPlaybackRateChange?.(next);
  }, [controlledPlaybackRate, onPlaybackRateChange, playbackRate]);

  const updateDragFromX = React.useCallback((x, width) => {
    if (!width || !durationMillis) return;
    const clampedX = Math.max(0, Math.min(width, x));
    const ratio = clampedX / width;
    dragRatioRef.current = ratio;
    setDragRatio(ratio);
  }, [durationMillis]);

  const commitSeekFromRatio = React.useCallback((ratio) => {
    if (!player || !durationMillis) return;
    try {
      // Convert to seconds for expo-audio
      const durationSeconds = durationMillis / 1000;
      const targetSeconds = Math.max(0, Math.min(durationSeconds, durationSeconds * ratio));
      player.seekTo(targetSeconds);
    } catch (e) {
      // console.log('commitSeekFromRatio error', e);
    }
  }, [player, durationMillis]);

  const finishDrag = React.useCallback(async () => {
    setIsDragging(false);
    const ratio = typeof dragRatioRef.current === 'number' ? dragRatioRef.current : progressRatio;
    dragRatioRef.current = null;
    setDragRatio(null);
    await commitSeekFromRatio(ratio);
  }, [commitSeekFromRatio, progressRatio]);

  const swallowCardPress = React.useCallback((e) => {
    e?.stopPropagation?.();
    e?.nativeEvent?.stopPropagation?.();
  }, []);

  const seekFromWebClick = React.useCallback((e, widthFallback) => {
    swallowCardPress(e);
    if (Platform.OS !== 'web') return;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    const ratio = seekRatioFromClientX(e.clientX, rect || { left: 0, width: widthFallback });
    if (ratio == null) return;
    dragRatioRef.current = ratio;
    setDragRatio(ratio);
    commitSeekFromRatio(ratio);
  }, [commitSeekFromRatio, swallowCardPress]);

  const trackResponder = React.useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      updateDragFromX(evt.nativeEvent.locationX, trackWidth);
    },
    onPanResponderMove: (evt) => {
      updateDragFromX(evt.nativeEvent.locationX, trackWidth);
    },
    onPanResponderRelease: finishDrag,
    onPanResponderTerminate: finishDrag,
  }), [finishDrag, trackWidth, updateDragFromX]);

  const waveResponder = React.useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      updateDragFromX(evt.nativeEvent.locationX, waveWidth);
    },
    onPanResponderMove: (evt) => {
      updateDragFromX(evt.nativeEvent.locationX, waveWidth);
    },
    onPanResponderRelease: finishDrag,
    onPanResponderTerminate: finishDrag,
  }), [finishDrag, updateDragFromX, waveWidth]);

  if (Platform.OS === 'macos' && status?.error) {
    return (
      <View style={styles.root}>
        <View style={[styles.inner, { alignItems: 'center' }]}>
          <Text size="xs" color="secondary">Could not load audio.</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Retry audio"
            onPress={(event) => { event?.stopPropagation?.(); player.replace(sourceUrl); }}
            style={{ paddingHorizontal: 12, paddingVertical: 8 }}
          >
            <Text size="xs">Retry audio</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <View style={styles.inner}>
        <View
          style={[styles.waveArea, { height: waveHeight }]}
          onLayout={(e) => setWaveWidth(Math.round(e.nativeEvent.layout.width))}
          onClick={(e) => seekFromWebClick(e, waveWidth)}
          {...waveResponder.panHandlers}
        >
          <Svg
            width={size - 24}
            height={waveHeight}
            viewBox={`0 0 ${size - 24} ${waveHeight}`}
            pointerEvents="none"
          >
            {(() => {
              const width = size - 24;
              const height = waveHeight;
              const maxBarHeight = height * 0.9;
              const barWidth = width / numBars * 0.6;
              const gap = (width - barWidth * numBars) / (numBars + 1);
              const baseline = height / 2;
              const elems = [];
              for (let i = 0; i < numBars; i++) {
                const x = gap + i * (barWidth + gap);
                const h = Math.max(3, Math.round(maxBarHeight * barData[i]));
                const y = baseline - h / 2;
                const isPast = (i / numBars) <= displayRatio;
                elems.push(
                  <Rect
                    key={`bar-${i}`}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    rx={barWidth * 0.3}
                    fill={isPast ? colors.text_color_primary : colors.text_color_secondary}
                    opacity={isPast ? 0.95 : 0.6}
                  />
                );
              }
              const px = gap + Math.max(0, Math.min(numBars, Math.round(numBars * displayRatio))) * (barWidth + gap);
              elems.push(
                <Line key="progress-line" x1={px} x2={px} y1={height * 0.15} y2={height * 0.85} stroke={colors.text_color_alt} strokeWidth={2} opacity={0.8} />
              );
              return elems;
            })()}
          </Svg>
        </View>
        <View style={styles.controlsRow}>
          <Pressable style={styles.playButton} onPress={togglePlayPause} accessibilityRole="button" accessibilityLabel={isPlaying ? 'Pause audio' : 'Play audio'}>
            <Icon name={isPlaying ? 'pause' : 'play'} size={16} color="#fff" />
          </Pressable>
          <View style={styles.trackWrapper}>
            <View
              style={styles.trackBg}
              ref={trackBgRef}
              onLayout={(e) => setTrackWidth(Math.round(e.nativeEvent.layout.width))}
            >
              <View style={[styles.trackFill, { width: `${displayRatio * 100}%` }]} />
              {!!trackWidth && (
                <View
                  style={[styles.trackKnob, { left: Math.max(0, Math.min(trackWidth - 14, (displayRatio * trackWidth) - 7)) }]}
                />
              )}
              {/* Hover overlay and drag handlers */}
              <View
                style={StyleSheet.absoluteFill}
                {...trackResponder.panHandlers}
                onClick={(e) => seekFromWebClick(e, trackWidth)}
                onMouseEnter={() => { if (Platform.OS === 'web') setIsHovering(true); }}
                onMouseLeave={() => { if (Platform.OS === 'web') { setIsHovering(false); setHoverRatio(null); } }}
                onMouseMove={(e) => {
                  if (Platform.OS !== 'web') return;
                  try {
                    const rect = e.currentTarget.getBoundingClientRect?.();
                    const x = typeof e.clientX === 'number' && rect ? (e.clientX - rect.left) : 0;
                    const w = rect?.width || trackWidth || 0;
                    if (w > 0) {
                      const r = Math.max(0, Math.min(1, x / w));
                      setHoverRatio(r);
                    }
                  } catch (err) {
                    // console.log('onMouseMove error', err);
                  }
                }}
              />
              {Platform.OS === 'web' && isHovering && !isDragging && typeof hoverRatio === 'number' && !!trackWidth && (
                <View
                  style={[styles.trackGhostKnob, { left: Math.max(0, Math.min(trackWidth - 14, (hoverRatio * trackWidth) - 7)) }]}
                  pointerEvents="none"
                />
              )}
            </View>
          </View>
          <Pressable style={styles.volumeButton} onPress={cycleVolume} accessibilityRole="button" accessibilityLabel={`Change volume, currently ${Math.round(volume * 100)} percent`}>
            <Icon name={volumeIcon} size={16} color="#fff" />
          </Pressable>
          <Pressable
            style={styles.speedButton}
            onPress={cyclePlaybackRate}
            accessibilityRole="button"
            accessibilityLabel={`Playback speed ${playbackRate} times`}
          >
            <Text size="xxs" style={{ color: '#fff' }}>{playbackRate}×</Text>
          </Pressable>
        </View>
        {Platform.OS === 'macos' && isLarge ? (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 12, marginTop: 8 }}>
            <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatTime(positionMillis)}
            </Text>
            <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatTime(durationMillis)}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
});

export default AudioWaveformPlayer;
